import { Component, OnInit } from '@angular/core';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { DemandRequestDetailService } from '../demand-request-detail.service';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { RequestTypeBaseComponent } from '../request-type-base/request-type-base.component';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestDto } from '../../../xatena-api';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-type-investment-exceptional',
  templateUrl: './request-type-investment-exceptional.component.html',
  styleUrls: ['./request-type-investment-exceptional.component.scss'],
  imports: [FormlyModule, AsyncPipe],
})
export class RequestTypeInvestmentExceptionalSpzComponent
  extends RequestTypeBaseComponent
  implements OnInit
{
  subs = new SubSink();

  public get rekoleCategorySelectList$() {
    return this.demandRequestDetailService.rekoleCategorySelectList$;
  }

  constructor(
    private translateService: TranslateService,
    demandRequestDetailService: DemandRequestDetailService
  ) {
    super(demandRequestDetailService);
  }

  private updateTotalCost(request: RequestDto) {
    const costInvestmentTotal =
      +(request.costNet ?? 0) +
      +(request.costConstruction ?? 0) +
      +(request.costInstallation ?? 0) +
      +(request.costIT ?? 0) +
      +(request.costTraining ?? 0) +
      +(request.costOthers ?? 0);

    this.form.get('costInvestmentTotal')?.setValue(costInvestmentTotal, {
      emitEvent: false,
    });

    const costUsageYears = +(request.costUsageYears ?? 0);
    const costRecurringTotal =
      +(request.costRecurringMaintenance ?? 0) +
      +(request.costRecurringConsumables ?? 0) +
      +(request.costRecurringOthers ?? 0);

    this.form.get('costRecurringTotal')?.setValue(costRecurringTotal, {
      emitEvent: false,
    });

    let costTotal = costInvestmentTotal;
    if (costRecurringTotal > 0 && costUsageYears > 0) {
      costTotal += costRecurringTotal * costUsageYears;
    }

    this.form.get('costTotal')?.setValue(costTotal, {
      emitEvent: false,
    });
  }

  ngOnInit(): void {
    this.subs.sink = this.form.valueChanges.subscribe(s => {
      this.updateTotalCost(s);
    });

    this.fields = [
      {
        hooks: {
          onInit: field => {
            this.subs.sink = this.isReadOnly$.subscribe(s => {
              if (s) {
                this.form.disable();
              } else {
                this.form.enable();
              }
            });
          },
        },
        hideExpression: model =>
          !(model.requestTypeId === RequestTypeEnum.InvestmentExceptional),
        fieldGroup: [
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_0_GRP_1.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'description',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.LABEL'),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'reason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REASON.LABEL'),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'product',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PRODUCT.LABEL'),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'location',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.LOCATION.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'projectNumber',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PROJECT_NUMBER.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'budgetYear',
                type: 'input',
                templateOptions: {
                  type: 'number',
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.BUDGET_YEAR.LABEL'),
                  translate: true,
                },
                validators: {
                  min: {
                    expression: (c: any, field: FormlyFieldConfig) => {
                      const minValue = new Date().getFullYear() - 1;

                      const res = Validators.min(minValue)(c);
                      if (!res) return { min: true };
                      return null;
                    },
                    message: (error: any, field: FormlyFieldConfig) => {
                      const minValue = new Date().getFullYear() - 1;

                      return this.translateService.stream(
                        'DEMAND_REQUEST.DETAIL.FORM.BUDGET_YEAR.VALIDATION.MIN',
                        { min: minValue }
                      );
                    },
                  },
                  max: {
                    expression: (c: any, field: FormlyFieldConfig) => {
                      const maxValue = new Date().getFullYear() + 6;

                      const res = Validators.max(maxValue)(c);
                      if (!res) return { max: true };
                      return null;
                    },
                    message: (error: any, field: FormlyFieldConfig) => {
                      const maxValue = new Date().getFullYear() + 6;

                      return this.translateService.stream(
                        'DEMAND_REQUEST.DETAIL.FORM.BUDGET_YEAR.VALIDATION.MAX',
                        { max: maxValue }
                      );
                    },
                  },
                },
              },
              {
                key: 'budgetPositionNumber',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.BUDGET_POSITION_NUMBER.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                className: 'rekole-select-container',
                key: 'rekoleCd',
                type: 'rekole-select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REKOLE_CATEGORY.LABEL'),
                  translate: true,
                  options: this.rekoleCategorySelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_0_GRP_2.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'replacing',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REPLACING.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'replacingInventoryNr',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.REPLACING_INVENTORY_NUMBER.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'replacingYear',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REPLACING_YEAR.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'replacingDecom',
                type: 'textarea',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REPLACING_DECOM.LABEL'),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 2,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_0_GRP_3.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'benefitSafety',
                type: 'checkbox',
                defaultValue: false,
                templateOptions: {
                  label: _('DEMAND_REQUEST.DETAIL.FORM.BENEFIT_SAFETY.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'benefitPerformance',
                type: 'checkbox',
                defaultValue: false,
                templateOptions: {
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.BENEFIT_PERFORMANCE.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'benefitInnovation',
                type: 'checkbox',
                defaultValue: false,
                templateOptions: {
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.BENEFIT_INNOVATION.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'benefit',
                type: 'textarea',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.BENEFIT.LABEL'),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 2,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'rejectionEffect',
                type: 'textarea',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REJECTION_EFFECT.LABEL'),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 2,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_0_GRP_4.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'amountText',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.AMOUNT_TEXT.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'costNet',
                type: 'currency',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_NET.LABEL'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_NET.DESCRIPTION'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costConstruction',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_CONSTRUCTION.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costInstallation',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_INSTALLATION.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                fieldGroupClassName: 'row',
                fieldGroup: [
                  {
                    className: 'col',
                    key: 'costIT',
                    type: 'currency',
                    templateOptions: {
                      required: false,
                      appearance: this.formFieldAppearance,
                      label: _('DEMAND_REQUEST.DETAIL.FORM.COST_IT.LABEL'),
                      translate: true,
                      currency: this.currency$,
                    },
                  },
                ],
              },
              {
                key: 'costTraining',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TRAINING.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'costOthers',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_OTHERS.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'costOthersDescription',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_OTHERS_DESCRIPTION.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costInvestmentTotal',
                type: 'currency',
                templateOptions: {
                  required: false,
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_INVESTMENT_TOTAL.LABEL'
                  ),
                  translate: true,
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_0_GRP_5.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'costRecurringMaintenance',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_RECURRING_MAINTENANCE.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costRecurringConsumables',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_RECURRING_CONSUMABLES.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costRecurringOthers',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_RECURRING_OTHERS.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costRecurringOthersDescription',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_RECURRING_OTHERS_DESCRIPTION.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'costRecurringTotal',
                type: 'currency',
                templateOptions: {
                  required: false,
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.COST_RECURRING_TOTAL.LABEL'
                  ),
                  translate: true,
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_0_GRP_6.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'costUsageYears',
                type: 'currency',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_USAGE_YEARS.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'costTotal',
                type: 'currency',
                templateOptions: {
                  required: false,
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TOTAL.LABEL'),
                  translate: true,
                  addonLeft: {
                    text: 'CHF',
                  },
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
